import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { AddressList, EmailTemplate } from '../../common/EmailDetails';
import { staggeredBaseQuery } from './staggeredBaseQuery';

export const emailApi = createApi({
  reducerPath: 'emailApi',
  tagTypes: ['Email', 'EmailTemplate'],
  baseQuery: staggeredBaseQuery(''),
  endpoints: (builder) => ({
    getAddressList: builder.query<AddressList, {}>({
      query: () => ({
        method: 'GET',
        url: `emails/addressList`,
      }),
      providesTags: [{ type: 'Email', id: 'LIST' }],
    }),
    sendEmail: builder.mutation<void, FormData>({
      query: (data) => ({
        method: 'POST',
        url: 'emails/send',
        body: data,
      }),
      invalidatesTags: [{ type: 'Email', id: 'LIST' }],
    }),
    getEmailTemplates: builder.query<EmailTemplate[], { emailType?: string; includeDeleted?: boolean }>({
      query: ({ emailType, includeDeleted }) => {
        const params = new URLSearchParams();
        if (emailType) params.set('emailType', emailType);
        if (includeDeleted) params.set('includeDeleted', 'true');
        const qs = params.toString();
        return { method: 'GET', url: `emailTemplates${qs ? `?${qs}` : ''}` };
      },
      providesTags: [{ type: 'EmailTemplate', id: 'LIST' }],
    }),
    getEmailTemplateById: builder.query<EmailTemplate, number>({
      query: (id) => ({ method: 'GET', url: `emailTemplates/${id}` }),
      providesTags: (_result, _err, id) => [{ type: 'EmailTemplate', id }],
    }),
    createEmailTemplate: builder.mutation<EmailTemplate, Partial<EmailTemplate>>({
      query: (body) => ({ method: 'POST', url: 'emailTemplates', body }),
      invalidatesTags: [{ type: 'EmailTemplate', id: 'LIST' }],
    }),
    updateEmailTemplate: builder.mutation<EmailTemplate, { id: number } & Partial<EmailTemplate>>({
      query: ({ id, ...body }) => ({ method: 'PUT', url: `emailTemplates/${id}`, body }),
      invalidatesTags: [{ type: 'EmailTemplate', id: 'LIST' }],
    }),
    deleteEmailTemplate: builder.mutation<void, number>({
      query: (id) => ({ method: 'DELETE', url: `emailTemplates/${id}` }),
      invalidatesTags: [{ type: 'EmailTemplate', id: 'LIST' }],
    }),
    restoreEmailTemplate: builder.mutation<void, number>({
      query: (id) => ({ method: 'PATCH', url: `emailTemplates/${id}/restore` }),
      invalidatesTags: [{ type: 'EmailTemplate', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetAddressListQuery,
  useSendEmailMutation,
  useGetEmailTemplatesQuery,
  useGetEmailTemplateByIdQuery,
  useCreateEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
  useRestoreEmailTemplateMutation,
} = emailApi;